/**
 * Art markup: a drawing written as XML, compiled once and painted every frame.
 *
 * ```xml
 * <Circle r="60" stroke="accent" strokeWidth="2" glow="accent" glowSize="12"/>
 * <Repeat count="7" rotate="i*360/n">
 *   <Line x1="0" y1="-70" x2="0" y2="-84" stroke="accent"/>
 * </Repeat>
 * ```
 *
 * Every number or color an element takes may be an expression (`expr.ts`), read with the names in
 * scope where it is drawn - `t`, the size, a generator's index, a definition's parameters, the
 * document's variables, the theme's palette. So a starfield is a `<Scatter>` of circles whose
 * opacity is a sine of the time, and a globe is a `<Sphere>` with lines drawn on it: nothing about
 * either is written in TypeScript.
 *
 * The renderer has no rotation and no paths. Shapes the renderer can draw exactly - a circle, an
 * upright rect, an arc, a line - go straight to it; anything turned, skewed or bent is flattened to
 * points here and filled or stroked as triangles (`shapes.ts`).
 */
import { fade, mixColor, type Color } from "../../../common/color.js";
import type { Point, Rect } from "../../../common/geometry.js";
import { MarkupError, type MarkupPosition } from "../../markup/errors.js";
import type { XmlElement } from "../../markup/xml.js";
import type { FontSpec } from "../../render/GlyphAtlas.js";
import type { DrawableTexture, RenderContext } from "../../render/RenderContext.js";
import type { Theme } from "../../themes/Theme.js";
import { onSphere, orbitPoint } from "../geometry.js";
import {
  compileExpression,
  ExpressionError,
  hashOf,
  isColor,
  toColor,
  toNumber,
  type Expression,
  type Scope,
  type Value,
} from "./expr.js";
import * as S from "./shapes.js";

// --- the vocabulary ----------------------------------------------------------------------------

const TRANSFORM = ["x", "y", "rotate", "scale", "scaleX", "scaleY", "pivotX", "pivotY", "opacity", "visible", "name"];
const PAINT = ["fill", "stroke", "strokeWidth", "dash", "dashOffset", "glow", "glowSize", "reveal", "cap"];

/** What each element takes beyond `TRANSFORM`. Anything else on it is an error, named. */
const ELEMENTS: Record<string, readonly string[]> = {
  Group: [],
  Clip: ["width", "height"],
  Rect: [...PAINT, "width", "height", "rx", "ry"],
  Circle: [...PAINT, "cx", "cy", "r", "start"],
  Ellipse: [...PAINT, "cx", "cy", "rx", "ry"],
  Line: [...PAINT, "x1", "y1", "x2", "y2"],
  Polyline: [...PAINT, "points"],
  Polygon: [...PAINT, "points"],
  Arc: [...PAINT, "cx", "cy", "r", "start", "end", "thickness"],
  Star: [...PAINT, "cx", "cy", "r", "points", "step", "inner", "turn"],
  Diamond: [...PAINT, "cx", "cy", "width", "height"],
  Path: [...PAINT, "d"],
  Text: ["fill", "text", "value", "format", "decimals", "size", "weight", "family", "align"],
  Image: ["src", "width", "height", "tint"],
  Sprite: ["src", "width", "height", "frameWidth", "frameHeight", "frames", "fps", "frame", "loop", "tint"],
  Repeat: ["count", "as"],
  Scatter: ["count", "seed", "width", "height", "as"],
  Particles: ["count", "seed", "life", "width", "height", "vx", "vy", "gx", "gy", "as"],
  Sphere: ["cx", "cy", "r", "turn", "pitch", "roll", "side"],
  Meridians: [...PAINT, "count", "samples"],
  Parallels: [...PAINT, "step", "limit", "samples"],
  Walk: [...PAINT, "steps", "heading", "turn", "step", "dx", "dy", "limit"],
  Orbit: ["cx", "cy", "r", "incline", "node", "angle", "side"],
  OrbitPath: [...PAINT, "cx", "cy", "r", "incline", "node", "side", "samples"],
  Use: ["ref", "width", "height"],
  Let: ["value"],
};

/** Attributes read as text rather than as an expression. */
const RAW = new Set(["d", "points", "ref", "as", "side", "dash", "align", "text", "format", "src", "frames", "cap", "family", "name"]);

/** Elements whose x and y move what they hold, rather than describing a shape. */
const PLACES = new Set(["Group", "Clip", "Use", "Text", "Image", "Sprite"]);

export const ART_ELEMENTS: readonly string[] = [...Object.keys(ELEMENTS), "Animate"];

const ANIMATE_ATTRIBUTES = ["attr", "from", "to", "dur", "delay", "ease", "repeat", "trigger", "name"];

/** The attributes a drawing element takes, for an editor offering them; null for a name that is not one. */
export function artElementAttributes(tag: string): readonly string[] | null {
  if (tag === "Animate") return ANIMATE_ATTRIBUTES;
  const own = Object.hasOwn(ELEMENTS, tag) ? ELEMENTS[tag] : undefined;
  return own ? [...own, ...TRANSFORM] : null;
}

type Curve = "linear" | "in" | "out" | "inOut" | "smooth" | "back";

interface AnimateSpec {
  attr: string;
  from: Expression;
  to: Expression;
  dur: number;
  delay: number;
  curve: Curve;
  repeat: "once" | "loop" | "bounce";
  trigger: "load" | "hover" | "press" | "lit" | "focus" | "manual";
  name: string | null;
}

type PaintSpec =
  | { kind: "solid"; color: Expression }
  | { kind: "linear"; angle: Expression; from: Expression; to: Expression }
  | { kind: "radial"; from: Expression; to: Expression; cx: Expression | null; cy: Expression | null; rx: Expression | null; ry: Expression | null };

export interface DrawNode {
  readonly tag: string;
  readonly at: MarkupPosition;
  readonly exprs: ReadonlyMap<string, Expression>;
  readonly raw: ReadonlyMap<string, string>;
  readonly fill: PaintSpec | null;
  readonly stroke: PaintSpec | null;
  /** A `<Use>`'s parameters, which may be any name. */
  readonly params: ReadonlyMap<string, Expression>;
  readonly children: readonly DrawNode[];
  readonly animations: readonly AnimateSpec[];
  /** Everything a frame of it reads, deepest children included. */
  readonly names: ReadonlySet<string>;
  /** Geometry that does not change: a constant `d`, a constant `points`. */
  cache: { path?: S.Subpath[]; points?: Point[]; exprPoints?: [Expression, Expression][] };
}

/** A drawing, compiled. `names` is every name anything in it reads, so a host can tell whether it moves. */
export interface Drawing {
  readonly nodes: readonly DrawNode[];
  readonly names: ReadonlySet<string>;
  readonly animated: boolean;
}

// --- compiling -------------------------------------------------------------------------------

function expr(source: string, at: MarkupPosition, what: string): Expression {
  try {
    return compileExpression(source);
  } catch (e) {
    if (e instanceof ExpressionError) throw new MarkupError(`${what}: ${e.message}`, at);
    throw e;
  }
}

function splitArgs(inner: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i];
    if (c === "(") depth++;
    else if (c === ")") depth--;
    else if (c === "," && depth === 0) {
      out.push(inner.slice(start, i).trim());
      start = i + 1;
    }
  }
  out.push(inner.slice(start).trim());
  return out;
}

function paintSpec(value: string, at: MarkupPosition, what: string): PaintSpec | null {
  const v = value.trim();
  if (v === "none" || v === "") return null;
  const m = /^(linear|radial)\((.*)\)$/s.exec(v);
  if (m) {
    const args = splitArgs(m[2]!).map((a, i) => expr(a, at, `${what} argument ${i + 1}`));
    if (m[1] === "linear") {
      if (args.length !== 3) throw new MarkupError(`${what}: linear(angle, from, to) takes three arguments`, at);
      return { kind: "linear", angle: args[0]!, from: args[1]!, to: args[2]! };
    }
    if (args.length !== 2 && args.length !== 4 && args.length !== 6) {
      throw new MarkupError(`${what}: radial(from, to[, cx, cy[, rx, ry]]) takes two, four or six arguments`, at);
    }
    return { kind: "radial", from: args[0]!, to: args[1]!, cx: args[2] ?? null, cy: args[3] ?? null, rx: args[4] ?? null, ry: args[5] ?? null };
  }
  return { kind: "solid", color: expr(v, at, what) };
}

const CURVES: readonly Curve[] = ["linear", "in", "out", "inOut", "smooth", "back"];

function compileAnimate(el: XmlElement, owner: string): AnimateSpec {
  const known = ["attr", "from", "to", "dur", "delay", "ease", "repeat", "trigger", "name"];
  const get = (n: string) => el.attributes.find((a) => a.name === n);
  for (const a of el.attributes) {
    if (!known.includes(a.name)) throw new MarkupError(`<Animate> has no "${a.name}" attribute; it takes ${known.join(", ")}`, a);
  }
  if (el.children.length) throw new MarkupError(`<Animate> takes no children`, el);
  const attr = get("attr")?.value;
  if (!attr) throw new MarkupError(`<Animate> needs attr: the attribute of <${owner}> it moves`, el);
  const num = (n: string, d: number) => {
    const a = get(n);
    if (!a) return d;
    const v = Number(a.value);
    if (!Number.isFinite(v) || v < 0) throw new MarkupError(`<Animate> ${n} is seconds, not "${a.value}"`, a);
    return v;
  };
  const pick = <T extends string>(n: string, options: readonly T[], d: T): T => {
    const a = get(n);
    if (!a) return d;
    if (!options.includes(a.value as T)) throw new MarkupError(`<Animate> ${n} is ${options.join(", ")}, not "${a.value}"`, a);
    return a.value as T;
  };
  const from = get("from");
  const to = get("to");
  if (!from || !to) throw new MarkupError(`<Animate> needs both from and to`, el);
  return {
    attr,
    from: expr(from.value, from, "<Animate> from"),
    to: expr(to.value, to, "<Animate> to"),
    dur: Math.max(1e-3, num("dur", 0.5)),
    delay: num("delay", 0),
    curve: pick("ease", CURVES, "smooth"),
    repeat: pick("repeat", ["once", "loop", "bounce"] as const, "once"),
    trigger: pick("trigger", ["load", "hover", "press", "lit", "focus", "manual"] as const, "load"),
    name: get("name")?.value ?? null,
  };
}

/** Compile one drawing element and everything in it. Throws a positioned `MarkupError` on anything wrong. */
export function compileNode(el: XmlElement): DrawNode {
  const allowed = ELEMENTS[el.name];
  if (!allowed) {
    throw new MarkupError(`there is no <${el.name}> in a drawing; try ${Object.keys(ELEMENTS).join(", ")}`, el);
  }
  const exprs = new Map<string, Expression>();
  const raw = new Map<string, string>();
  const params = new Map<string, Expression>();
  let fill: PaintSpec | null = null;
  let stroke: PaintSpec | null = null;
  const names = new Set<string>();
  const take = (e: Expression) => e.names.forEach((n) => names.add(n));
  const takePaint = (p: PaintSpec | null) => {
    if (!p) return;
    for (const v of Object.values(p)) if (v && typeof v === "object" && "evaluate" in v) take(v as Expression);
  };

  for (const a of el.attributes) {
    const isParam = el.name === "Use" && !TRANSFORM.includes(a.name) && !allowed.includes(a.name);
    if (isParam) {
      const e = expr(a.value, a, `<Use> ${a.name}`);
      params.set(a.name, e);
      take(e);
      continue;
    }
    if (!TRANSFORM.includes(a.name) && !allowed.includes(a.name)) {
      throw new MarkupError(`<${el.name}> has no "${a.name}" attribute; it takes ${[...allowed, ...TRANSFORM].join(", ")}`, a);
    }
    if (a.name === "fill" || a.name === "stroke") {
      const p = paintSpec(a.value, a, `<${el.name}> ${a.name}`);
      if (a.name === "fill") fill = p;
      else stroke = p;
      takePaint(p);
      continue;
    }
    // A star's points is a count; everywhere else it is a list of coordinates.
    if (RAW.has(a.name) && !(el.name === "Star" && a.name === "points")) {
      raw.set(a.name, a.value);
      continue;
    }
    const e = expr(a.value, a, `<${el.name}> ${a.name}`);
    exprs.set(a.name, e);
    take(e);
  }
  if (el.name === "Let" && (!raw.get("name") || !exprs.has("value"))) {
    throw new MarkupError(`<Let> needs a name and a value`, el);
  }
  if (el.name === "Use" && !raw.get("ref")) throw new MarkupError(`<Use> needs ref: the name of a <Define>`, el);
  if (el.text && el.name !== "Text") throw new MarkupError(`<${el.name}> takes no text`, el);
  if (el.name === "Text" && el.text && !raw.has("text")) raw.set("text", el.text);
  const side = raw.get("side");
  if (side !== undefined && !["front", "back", "both"].includes(side)) {
    throw new MarkupError(`side is front, back or both, not "${side}"`, el);
  }

  const children: DrawNode[] = [];
  const animations: AnimateSpec[] = [];
  for (const child of el.children) {
    if (child.name === "Animate") {
      const a = compileAnimate(child, el.name);
      take(a.from);
      take(a.to);
      names.add("t");
      animations.push(a);
      continue;
    }
    const node = compileNode(child);
    node.names.forEach((n) => names.add(n));
    children.push(node);
  }
  if (el.name === "Particles") names.add("t");
  if (el.name === "Sprite") names.add("t");

  const node: DrawNode = { tag: el.name, at: el, exprs, raw, fill, stroke, params, children, animations, names, cache: {} };
  const d = raw.get("d");
  if (d !== undefined) {
    try {
      node.cache.path = S.flattenPath(d);
    } catch (e) {
      throw new MarkupError(`<Path> d: ${(e as Error).message}`, el);
    }
  }
  const points = raw.get("points");
  if (points !== undefined && el.name !== "Star") {
    if (points.includes(";")) {
      node.cache.exprPoints = points
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p, i) => {
          const xy = splitArgs(p);
          if (xy.length !== 2) throw new MarkupError(`point ${i + 1} of "${points}" is not "x, y"`, el);
          return [expr(xy[0]!, el, `point ${i + 1} x`), expr(xy[1]!, el, `point ${i + 1} y`)] as [Expression, Expression];
        });
      for (const [x, y] of node.cache.exprPoints) {
        x.names.forEach((n) => names.add(n));
        y.names.forEach((n) => names.add(n));
      }
    } else {
      node.cache.points = parsePoints(points, el);
    }
  }
  return node;
}

/** A node's points: constant ones as parsed, or `"x, y; x, y"` expressions read where it is drawn. */
function pointsOf(node: DrawNode, state: State): Point[] {
  if (node.cache.points) return node.cache.points;
  return (node.cache.exprPoints ?? []).map(([x, y]) => ({
    x: toNumber(x.evaluate(state.scope)),
    y: toNumber(y.evaluate(state.scope)),
  }));
}

/** `"0,0 10,0 10,10"` - numbers in pairs, the way SVG writes them. */
function parsePoints(text: string, at: MarkupPosition): Point[] {
  const nums = text.split(/[\s,]+/).filter((s) => s.length > 0).map(Number);
  if (nums.some((n) => !Number.isFinite(n)) || nums.length % 2 !== 0) {
    throw new MarkupError(`points are numbers in pairs, "x,y x,y ...": "${text}"`, at);
  }
  const out: Point[] = [];
  for (let i = 0; i < nums.length; i += 2) out.push({ x: nums[i]!, y: nums[i + 1]! });
  return out;
}

/** Compile the children of an element as one drawing. */
export function compileDrawing(elements: readonly XmlElement[]): Drawing {
  const nodes = elements.map(compileNode);
  const names = new Set<string>();
  for (const n of nodes) n.names.forEach((x) => names.add(x));
  return { nodes, names, animated: names.has("t") };
}

// --- definitions -----------------------------------------------------------------------------

/**
 * What a `<Param>` says about a parameter, for whoever offers it to a person: a label, an editor,
 * a range. None of it changes what the drawing does - a parameter with no `<Param>` is just as live.
 */
export interface ParamInfo {
  label?: string;
  kind?: "number" | "color" | "boolean" | "choice";
  min?: number;
  max?: number;
  step?: number;
  options?: string[];
  /** A sentence about it, for documentation. */
  help?: string;
}

/** A `<Define>`: a drawing with parameters, placed by `<Use ref>` or `<Art ref>`. */
export interface Definition {
  readonly name: string;
  /** Parameters in the order declared, each with its default, which may read earlier ones. */
  readonly params: readonly { name: string; value: Expression }[];
  readonly viewBox: Rect | null;
  /** Px past its box the drawing paints - a halo - as an expression of `w` and `h`. */
  readonly overflow: Expression | null;
  /** What `<Param>` children say about the parameters, by name. */
  readonly info: ReadonlyMap<string, ParamInfo>;
  /** The definition's own `label`, for a picker; its name when it has none. */
  readonly label: string;
  readonly drawing: Drawing;
}

const definitions = new Map<string, Definition>();
const definitionListeners = new Set<() => void>();

export function parseViewBox(text: string, at: MarkupPosition): Rect {
  const n = text.split(/[\s,]+/).filter(Boolean).map(Number);
  if (n.length !== 4 || n.some((v) => !Number.isFinite(v)) || n[2]! <= 0 || n[3]! <= 0) {
    throw new MarkupError(`viewBox is "x y width height" with a positive size, not "${text}"`, at);
  }
  return { x: n[0]!, y: n[1]!, width: n[2]!, height: n[3]! };
}

/** Compile a `<Define>` element. Its attributes other than name and viewBox are its parameters. */
export function compileDefinition(el: XmlElement): Definition {
  const name = el.attributes.find((a) => a.name === "name")?.value;
  if (!name) throw new MarkupError(`<Define> needs a name`, el);
  const params: { name: string; value: Expression }[] = [];
  let viewBox: Rect | null = null;
  let overflow: Expression | null = null;
  let label = name;
  for (const a of el.attributes) {
    if (a.name === "name") continue;
    if (a.name === "label") {
      label = a.value;
      continue;
    }
    if (a.name === "overflow") {
      overflow = expr(a.value, a, `<Define name="${name}"> overflow`);
      continue;
    }
    if (a.name === "viewBox") {
      viewBox = parseViewBox(a.value, a);
      continue;
    }
    params.push({ name: a.name, value: expr(a.value, a, `<Define name="${name}"> ${a.name}`) });
  }
  const info = new Map<string, ParamInfo>();
  const body: XmlElement[] = [];
  for (const child of el.children) {
    if (child.name !== "Param") {
      body.push(child);
      continue;
    }
    const get = (n: string) => child.attributes.find((a) => a.name === n)?.value;
    const p = get("name");
    if (!p || !params.some((q) => q.name === p)) {
      throw new MarkupError(`<Param> names "${p ?? ""}", which <Define name="${name}"> does not declare`, child);
    }
    for (const a of child.attributes) {
      if (!["name", "label", "kind", "min", "max", "step", "options", "help"].includes(a.name)) {
        throw new MarkupError(`<Param> has no "${a.name}" attribute`, a);
      }
    }
    const n = (k: string) => (get(k) === undefined ? undefined : Number(get(k)));
    const kind = get("kind");
    if (kind !== undefined && !["number", "color", "boolean", "choice"].includes(kind)) {
      throw new MarkupError(`<Param> kind is number, color, boolean or choice, not "${kind}"`, child);
    }
    const entry: ParamInfo = {};
    if (get("label") !== undefined) entry.label = get("label")!;
    if (kind) entry.kind = kind as ParamInfo["kind"];
    if (n("min") !== undefined) entry.min = n("min")!;
    if (n("max") !== undefined) entry.max = n("max")!;
    if (n("step") !== undefined) entry.step = n("step")!;
    if (get("options")) entry.options = get("options")!.split("|").map((o) => o.trim()).filter(Boolean);
    if (get("help")) entry.help = get("help")!;
    info.set(p, entry);
  }
  return { name, params, viewBox, overflow, info, label, drawing: compileDrawing(body) };
}

/** Register a definition under its name, replacing one already there. */
export function defineArt(definition: Definition): void {
  definitions.set(definition.name, definition);
  for (const l of definitionListeners) l();
}

export function getArtDefinition(name: string): Definition | null {
  return definitions.get(name) ?? null;
}

export function artDefinitionNames(): string[] {
  return [...definitions.keys()].sort();
}

/** Called when a definition is added or replaced, so a drawing showing one can repaint. */
export function onArtDefinitionsChanged(listener: () => void): () => void {
  definitionListeners.add(listener);
  return () => definitionListeners.delete(listener);
}

// --- painting -----------------------------------------------------------------------------------

/** What a drawing is painted with: where, at what time and size, and what else it can read. */
export interface ArtEnvironment {
  rc: RenderContext;
  theme: Theme;
  /** Seconds on the drawing's clock. */
  t: number;
  /** The box the drawing fills, in its own units. */
  w: number;
  h: number;
  seed?: number;
  /** Names beyond the built-ins: a control's state, a document's variables, a look's color. */
  lookup?: (name: string) => Value | undefined;
  /** Kept between frames by whoever owns the drawing: eased state animations live here. */
  memory?: Map<object, { value: number; at: number }>;
  /** When a manual animation was last played, by name, on the drawing's clock. */
  played?: (name: string) => number | undefined;
  /** A picture by address, or null while it loads. */
  image?: (src: string) => DrawableTexture | null;
  /** The matrix from the drawing's units to the control's local pixels. */
  matrix?: S.Matrix;
}

const NAMED: Record<string, Color> = {
  transparent: { r: 0, g: 0, b: 0, a: 0 },
  white: { r: 1, g: 1, b: 1, a: 1 },
  black: { r: 0, g: 0, b: 0, a: 1 },
};

interface Projection {
  /** Lon and lat in degrees, already through `pm`, to the sphere's own space with depth. */
  project(p: Point): { x: number; y: number; z: number };
  side: "front" | "back" | "both";
  /** Matrix applied before projecting: positions on the sphere's surface. */
  pm: S.Matrix;
}

interface State {
  env: ArtEnvironment;
  /** Node units to local px - after any projection. */
  m: S.Matrix;
  scope: Scope;
  /** The scope a definition's body starts from: the built-ins, never a caller's locals. */
  root: Scope;
  sphere: Projection | null;
  depth: number;
}

const withVars = (parent: Scope, vars: Record<string, Value>): Scope => (name) =>
  Object.hasOwn(vars, name) ? vars[name] : parent(name);

/** The scope at the top of a drawing: the clock and the size, then the host's names, then the palette. */
export function rootScope(env: ArtEnvironment): Scope {
  const palette = env.theme.palette as unknown as Record<string, Color | undefined>;
  return (name) => {
    switch (name) {
      case "t":
        return env.t;
      case "w":
        return env.w;
      case "h":
        return env.h;
      case "seed":
        return env.seed ?? 0;
    }
    const hosted = env.lookup?.(name);
    if (hosted !== undefined) return hosted;
    if (name === "reveal") return 1;
    if (Object.hasOwn(NAMED, name)) return NAMED[name];
    const swatch = Object.hasOwn(palette, name) ? palette[name] : undefined;
    return swatch && typeof swatch === "object" ? swatch : undefined;
  };
}

/** Paint a compiled drawing. */
export function paintDrawing(drawing: Drawing | readonly DrawNode[], env: ArtEnvironment): void {
  const root = rootScope(env);
  const nodes = "nodes" in drawing ? drawing.nodes : drawing;
  paintList(nodes, { env, m: env.matrix ?? S.IDENTITY, scope: root, root, sphere: null, depth: 0 });
}

/**
 * Paint siblings in order. A `<Let>` among them names a value for the siblings after it and
 * everything inside them - how a copy works out its `life` once and reads it in five places.
 */
function paintList(nodes: readonly DrawNode[], state: State): void {
  let at = state;
  for (const node of nodes) {
    if (node.tag === "Let") {
      const v = node.exprs.get("value")!.evaluate(at.scope);
      at = { ...at, scope: withVars(at.scope, { [node.raw.get("name")!]: v }) };
      continue;
    }
    paintNode(node, at);
  }
}

const ease = (curve: Curve, x: number): number => {
  const k = Math.min(1, Math.max(0, x));
  switch (curve) {
    case "linear":
      return k;
    case "in":
      return k * k * k;
    case "out":
      return 1 - (1 - k) ** 3;
    case "inOut":
      return k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2;
    case "back": {
      const c = 1.70158;
      return 1 + (c + 1) * (k - 1) ** 3 + c * (k - 1) ** 2;
    }
    default:
      return k * k * (3 - 2 * k);
  }
};

function animationProgress(a: AnimateSpec, state: State): number {
  const t = state.env.t;
  const cycle = (s: number): number => {
    if (a.repeat === "loop") return s - Math.floor(s);
    if (a.repeat === "bounce") {
      const f = s / 2 - Math.floor(s / 2);
      return 1 - Math.abs(f * 2 - 1);
    }
    return Math.min(1, Math.max(0, s));
  };
  if (a.trigger === "load") return t < a.delay ? 0 : cycle((t - a.delay) / a.dur);
  if (a.trigger === "manual") {
    const from = a.name ? state.env.played?.(a.name) : undefined;
    if (from === undefined) return 0;
    const s = (t - from - a.delay) / a.dur;
    return s < 0 ? 0 : cycle(s);
  }
  // A state: eased toward 1 while it holds and back to 0 when it lets go.
  const flag = state.scope(a.trigger === "press" ? "pressed" : a.trigger === "hover" ? "hover" : a.trigger);
  const target = flag !== undefined && toNumber(flag) !== 0 ? 1 : 0;
  const memory = state.env.memory;
  if (!memory) return target;
  const kept = memory.get(a) ?? { value: target, at: t };
  const dt = Math.max(0, t - kept.at);
  const step = dt / a.dur;
  const value = target > kept.value ? Math.min(target, kept.value + step) : Math.max(target, kept.value - step);
  memory.set(a, { value, at: t });
  return value;
}

/** An attribute's value where it is drawn: an animation on it wins, then the attribute, then `fallback`. */
function value(node: DrawNode, name: string, state: State): Value | undefined {
  for (const a of node.animations) {
    if (a.attr !== name) continue;
    const k = ease(a.curve, animationProgress(a, state));
    const from = a.from.evaluate(state.scope);
    const to = a.to.evaluate(state.scope);
    if (isColor(from) || isColor(to)) return mixColor(toColor(from), toColor(to), k);
    return toNumber(from) + (toNumber(to) - toNumber(from)) * k;
  }
  return node.exprs.get(name)?.evaluate(state.scope);
}

function num(node: DrawNode, name: string, state: State, fallback: number): number {
  const v = value(node, name, state);
  if (v === undefined) return fallback;
  const n = toNumber(v);
  return Number.isFinite(n) ? n : fallback;
}

type Ink =
  | { kind: "solid"; color: Color }
  | { kind: "linear"; angle: number; from: Color; to: Color }
  | { kind: "radial"; from: Color; to: Color; center: [number, number]; radii: [number, number] };

function ink(node: DrawNode, which: "fill" | "stroke", state: State): Ink | null {
  for (const a of node.animations) {
    if (a.attr === which) {
      const v = value(node, which, state)!;
      return { kind: "solid", color: toColor(v) };
    }
  }
  const spec = which === "fill" ? node.fill : node.stroke;
  if (!spec) return null;
  const c = (e: Expression) => toColor(e.evaluate(state.scope));
  const n = (e: Expression | null, d: number) => (e ? toNumber(e.evaluate(state.scope)) : d);
  switch (spec.kind) {
    case "solid":
      return { kind: "solid", color: c(spec.color) };
    case "linear":
      return { kind: "linear", angle: n(spec.angle, 180), from: c(spec.from), to: c(spec.to) };
    default:
      return {
        kind: "radial",
        from: c(spec.from),
        to: c(spec.to),
        center: [n(spec.cx, 0.5), n(spec.cy, 0.5)],
        radii: [n(spec.rx, 0.5), n(spec.ry, spec.rx ? n(spec.rx, 0.5) : 0.5)],
      };
  }
}

/** The one color of a paint, for a shape that cannot take a gradient. */
const flat = (p: Ink): Color => (p.kind === "solid" ? p.color : mixColor(p.from, p.to, 0.5));

/** CSS's gradient angle - 180 runs top to bottom - as the renderer's axis in 0..1 quad space. */
function axisOf(angle: number): [number, number, number, number] {
  const r = (angle * Math.PI) / 180;
  const dx = Math.sin(r) / 2;
  const dy = -Math.cos(r) / 2;
  return [0.5 - dx, 0.5 - dy, 0.5 + dx, 0.5 + dy];
}

/** The matrix a node puts under its children, or under its own geometry. */
function localMatrix(node: DrawNode, state: State, pivot: Point): S.Matrix {
  const places = PLACES.has(node.tag);
  const x = places ? num(node, "x", state, 0) : 0;
  const y = places ? num(node, "y", state, 0) : 0;
  const rotate = num(node, "rotate", state, 0);
  const scale = num(node, "scale", state, 1);
  const sx = num(node, "scaleX", state, 1) * scale;
  const sy = num(node, "scaleY", state, 1) * scale;
  if (rotate === 0 && sx === 1 && sy === 1) return x === 0 && y === 0 ? S.IDENTITY : S.translation(x, y);
  const px = num(node, "pivotX", state, pivot.x);
  const py = num(node, "pivotY", state, pivot.y);
  let m = S.translation(x + px, y + py);
  if (rotate !== 0) m = S.multiply(m, S.rotation(rotate));
  if (sx !== 1 || sy !== 1) m = S.multiply(m, S.scaling(sx, sy));
  return S.multiply(m, S.translation(-px, -py));
}

/** `state` with `m` multiplied in where it belongs: before the projection on a sphere, after it off one. */
function under(state: State, m: S.Matrix, scope = state.scope): State {
  if (m === S.IDENTITY) return scope === state.scope ? state : { ...state, scope };
  if (state.sphere) return { ...state, scope, sphere: { ...state.sphere, pm: S.multiply(state.sphere.pm, m) } };
  return { ...state, scope, m: S.multiply(state.m, m) };
}

function paintNode(node: DrawNode, state: State): void {
  if (node.exprs.has("visible") || node.animations.some((a) => a.attr === "visible")) {
    if (num(node, "visible", state, 1) === 0) return;
  }
  const opacity = node.exprs.has("opacity") || node.animations.some((a) => a.attr === "opacity") ? num(node, "opacity", state, 1) : 1;
  if (opacity <= 0) return;
  const { rc } = state.env;
  if (opacity < 1) rc.pushOpacity(opacity);
  try {
    PAINTERS[node.tag]?.(node, state);
  } finally {
    if (opacity < 1) rc.popOpacity();
  }
}

const children = (node: DrawNode, state: State): void => paintList(node.children, state);

/** The copies a generator makes are told apart by this, so `rand` differs per copy and per nesting. */
function copyKey(state: State, i: number, salt: number): number {
  const parent = state.scope("__copy");
  return hashOf(typeof parent === "number" ? parent : 0, i, salt);
}

const MAX_COPIES = 5000;

type Painter = (node: DrawNode, state: State) => void;

const PAINTERS: Record<string, Painter> = {
  Group(node, state) {
    children(node, under(state, localMatrix(node, state, { x: 0, y: 0 })));
  },

  Clip(node, state) {
    const m = S.multiply(state.m, localMatrix(node, state, { x: 0, y: 0 }));
    const w = num(node, "width", state, state.env.w);
    const h = num(node, "height", state, state.env.h);
    const a = S.apply(m, { x: 0, y: 0 });
    const b = S.apply(m, { x: w, y: h });
    const r = { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.abs(b.x - a.x), height: Math.abs(b.y - a.y) };
    state.env.rc.clipped(r, () => children(node, { ...state, m }));
  },

  Repeat(node, state) {
    const n = Math.min(MAX_COPIES, Math.max(0, Math.floor(num(node, "count", state, 1))));
    const as = node.raw.get("as") ?? "i";
    for (let i = 0; i < n; i++) {
      const scope = withVars(state.scope, { [as]: i, n, __copy: copyKey(state, i, 1) });
      const copy = { ...state, scope };
      children(node, under(copy, localMatrix(node, copy, { x: 0, y: 0 })));
    }
  },

  Scatter(node, state) {
    const n = Math.min(MAX_COPIES, Math.max(0, Math.floor(num(node, "count", state, 50))));
    const seed = num(node, "seed", state, toNumber(state.scope("seed") ?? 0));
    const as = node.raw.get("as") ?? "i";
    const ax = num(node, "x", state, 0);
    const ay = num(node, "y", state, 0);
    const aw = num(node, "width", state, toNumber(state.scope("w") ?? 0));
    const ah = num(node, "height", state, toNumber(state.scope("h") ?? 0));
    for (let i = 0; i < n; i++) {
      const key = copyKey(state, i, 2);
      const u = hashOf(seed, key, 11);
      const v = hashOf(seed, key, 13);
      const scope = withVars(state.scope, { [as]: i, n, u, v, seed: seed + 0.5, __copy: key });
      const copy = { ...state, scope };
      const m = S.multiply(S.translation(ax + u * aw, ay + v * ah), localMatrixNoMove(node, copy));
      children(node, under(copy, m));
    }
  },

  Particles(node, state) {
    const n = Math.min(MAX_COPIES, Math.max(0, Math.floor(num(node, "count", state, 40))));
    const seed = num(node, "seed", state, toNumber(state.scope("seed") ?? 0));
    const as = node.raw.get("as") ?? "i";
    const t = state.env.t;
    const ax = num(node, "x", state, 0);
    const ay = num(node, "y", state, toNumber(state.scope("h") ?? 0));
    const aw = num(node, "width", state, toNumber(state.scope("w") ?? 0));
    const ah = num(node, "height", state, 0);
    for (let i = 0; i < n; i++) {
      const base = copyKey(state, i, 3);
      const phase = hashOf(seed, base, 17);
      // Life is read once per particle, so `life="2+rand(1)*3"` gives each its own.
      const lifeScope = withVars(state.scope, { [as]: i, n, __copy: base, seed: seed + 0.25 });
      const life = Math.max(0.05, num(node, "life", { ...state, scope: lifeScope }, 3));
      const run = t / life + phase;
      const cycle = Math.floor(run);
      const p = run - cycle;
      const age = p * life;
      // Each time round it is born somewhere new: the cycle is part of what makes it random.
      const key = hashOf(base, cycle, 19);
      const u = hashOf(seed, key, 11);
      const v = hashOf(seed, key, 13);
      const scope = withVars(state.scope, { [as]: i, n, p, age, life, u, v, seed: seed + 0.5, __copy: key });
      const copy = { ...state, scope };
      const vx = num(node, "vx", copy, 0);
      const vy = num(node, "vy", copy, -40);
      const gx = num(node, "gx", copy, 0);
      const gy = num(node, "gy", copy, 0);
      const px = ax + u * aw + vx * age + 0.5 * gx * age * age;
      const py = ay + v * ah + vy * age + 0.5 * gy * age * age;
      children(node, under(copy, S.multiply(S.translation(px, py), localMatrixNoMove(node, copy))));
    }
  },

  Use(node, state) {
    if (state.depth > 24) throw new MarkupError(`<Use ref="${node.raw.get("ref")}"> goes round in a circle`, node.at);
    const def = getArtDefinition(node.raw.get("ref")!);
    if (!def) return;
    const w = num(node, "width", state, toNumber(state.scope("w") ?? state.env.w));
    const h = num(node, "height", state, toNumber(state.scope("h") ?? state.env.h));
    const given: Record<string, Value> = {};
    for (const [k, e] of node.params) given[k] = e.evaluate(state.scope);
    let m = localMatrix(node, state, { x: w / 2, y: h / 2 });
    if (def.viewBox) m = S.multiply(m, fitMatrix(def.viewBox, w, h, "contain"));
    const body = definitionScope(def, state.root, given, def.viewBox ? def.viewBox.width : w, def.viewBox ? def.viewBox.height : h);
    paintList(def.drawing.nodes, { ...under(state, m), scope: body, depth: state.depth + 1 });
  },

  Rect(node, state) {
    const x = num(node, "x", state, 0);
    const y = num(node, "y", state, 0);
    const w = num(node, "width", state, 0);
    const h = num(node, "height", state, 0);
    if (w <= 0 || h <= 0) return;
    const rx = num(node, "rx", state, 0);
    const ry = num(node, "ry", state, rx);
    const m = S.multiply(state.m, localMatrix(node, state, { x: x + w / 2, y: y + h / 2 }));
    const fill = ink(node, "fill", state);
    const stroke = ink(node, "stroke", state);
    const plain = !state.sphere && S.isAxisAligned(m) && Math.abs(rx - ry) < 1e-9 && !node.raw.has("dash") && !node.exprs.has("reveal");
    if (plain) {
      const a = S.apply(m, { x, y });
      const b = S.apply(m, { x: x + w, y: y + h });
      const r = { x: a.x, y: a.y, width: b.x - a.x, height: b.y - a.y };
      const k = r.width / w;
      const radius = rx * k;
      const { rc } = state.env;
      glowRect(node, state, r, radius);
      if (fill) fillRect(rc, r, fill, radius);
      if (stroke) {
        const sw = num(node, "strokeWidth", state, 1) * k;
        const grown = { x: r.x - sw / 2, y: r.y - sw / 2, width: r.width + sw, height: r.height + sw };
        rc.drawRect(grown, { borderWidth: sw, borderColor: flat(stroke), radius: radius > 0 ? radius + sw / 2 : 0 });
      }
      return;
    }
    drawOutline(node, { ...state, m }, [{ points: S.rectPoints(x, y, w, h, rx, ry), closed: true }], fill, stroke);
  },

  Circle(node, state) {
    const cx = num(node, "cx", state, 0);
    const cy = num(node, "cy", state, 0);
    const r = num(node, "r", state, 0);
    if (r <= 0) return;
    const local = localMatrix(node, state, { x: cx, y: cy });
    if (state.sphere) {
      dot(node, under(state, local), cx, cy, r);
      return;
    }
    const m = S.multiply(state.m, local);
    const fill = ink(node, "fill", state);
    const stroke = ink(node, "stroke", state);
    if (!S.isConformal(m)) {
      drawOutline(node, { ...state, m }, [{ points: S.ellipsePoints(cx, cy, r, r), closed: true }], fill, stroke);
      return;
    }
    roundShape(node, state, S.apply(m, { x: cx, y: cy }), r * S.scaleOf(m), S.angleOf(m), fill, stroke, r);
  },

  Ellipse(node, state) {
    const cx = num(node, "cx", state, 0);
    const cy = num(node, "cy", state, 0);
    const rx = num(node, "rx", state, 0);
    const ry = num(node, "ry", state, rx);
    if (rx <= 0 || ry <= 0) return;
    const m = S.multiply(state.m, localMatrix(node, state, { x: cx, y: cy }));
    const fill = ink(node, "fill", state);
    const stroke = ink(node, "stroke", state);
    // A radial ramp is the one way the renderer squashes a circle, and fills its box with an ellipse.
    if (!state.sphere && fill?.kind === "radial" && !stroke && S.isAxisAligned(m)) {
      const a = S.apply(m, { x: cx - rx, y: cy - ry });
      const b = S.apply(m, { x: cx + rx, y: cy + ry });
      const r = { x: a.x, y: a.y, width: b.x - a.x, height: b.y - a.y };
      state.env.rc.fillRadialGradient(r, fill.from, fill.to, fill.center, fill.radii);
      return;
    }
    drawOutline(node, { ...state, m }, [{ points: S.ellipsePoints(cx, cy, rx, ry), closed: true }], fill, stroke);
  },

  Line(node, state) {
    const a = { x: num(node, "x1", state, 0), y: num(node, "y1", state, 0) };
    const b = { x: num(node, "x2", state, 0), y: num(node, "y2", state, 0) };
    const local = localMatrix(node, state, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    drawOutline(node, under(state, local), [{ points: [a, b], closed: false }], null, ink(node, "stroke", state));
  },

  Polyline(node, state) {
    const points = pointsOf(node, state);
    drawOutline(node, under(state, localMatrix(node, state, centerOf(points))), [{ points, closed: false }], ink(node, "fill", state), ink(node, "stroke", state));
  },

  Polygon(node, state) {
    const points = pointsOf(node, state);
    drawOutline(node, under(state, localMatrix(node, state, centerOf(points))), [{ points, closed: true }], ink(node, "fill", state), ink(node, "stroke", state));
  },

  Path(node, state) {
    const path = node.cache.path ?? [];
    const all = path.flatMap((s) => s.points);
    drawOutline(node, under(state, localMatrix(node, state, centerOf(all))), path, ink(node, "fill", state), ink(node, "stroke", state));
  },

  Arc(node, state) {
    const cx = num(node, "cx", state, 0);
    const cy = num(node, "cy", state, 0);
    const r = num(node, "r", state, 0);
    const start = num(node, "start", state, 0);
    const end = num(node, "end", state, 360);
    const reveal = num(node, "reveal", state, 1);
    if (r <= 0 || reveal <= 0) return;
    const to = start + (end - start) * Math.min(1, reveal);
    const local = localMatrix(node, state, { x: cx, y: cy });
    const m = S.multiply(state.m, local);
    const thickness = node.exprs.has("thickness") ? num(node, "thickness", state, 2) : null;
    const fill = ink(node, "fill", state);
    const stroke = ink(node, "stroke", state);
    if (thickness !== null) {
      // A band of the ring: what a sweep, a gauge or a dash of a dashed ring is.
      const color = fill ?? stroke;
      if (!color) return;
      if (!state.sphere && S.isConformal(m)) {
        const c = S.apply(m, { x: cx, y: cy });
        const k = S.scaleOf(m);
        const turn = S.angleOf(m);
        const lo = (Math.min(start, to) * Math.PI) / 180 + turn;
        const hi = (Math.max(start, to) * Math.PI) / 180 + turn;
        glowArc(node, state, c, (r - thickness / 2) * k, (r + thickness / 2) * k, lo, hi);
        state.env.rc.fillArc(c, (r - thickness / 2) * k, (r + thickness / 2) * k, lo, hi, flat(color));
        return;
      }
      const outer = S.arcPoints(cx, cy, r + thickness / 2, (start * Math.PI) / 180, (to * Math.PI) / 180);
      const inner = S.arcPoints(cx, cy, r - thickness / 2, (start * Math.PI) / 180, (to * Math.PI) / 180).reverse();
      drawOutline(node, under(state, local), [{ points: [...outer, ...inner], closed: true }], color, null, true);
      return;
    }
    const pts = S.arcPoints(cx, cy, r, (start * Math.PI) / 180, (to * Math.PI) / 180);
    drawOutline(node, under(state, local), [{ points: pts, closed: false }], null, stroke, true);
  },

  Star(node, state) {
    const cx = num(node, "cx", state, 0);
    const cy = num(node, "cy", state, 0);
    const r = num(node, "r", state, 0);
    const points = Math.max(2, Math.floor(num(node, "points", state, 5)));
    const turn = (num(node, "turn", state, 0) * Math.PI) / 180;
    const step = Math.floor(num(node, "step", state, 1));
    const local = localMatrix(node, state, { x: cx, y: cy });
    if (r <= 0) return;
    if (step > 1 && !node.exprs.has("inner")) {
      drawOutline(node, under(state, local), S.starStrokePaths(cx, cy, points, step, r, turn), null, ink(node, "stroke", state));
      return;
    }
    const inner = node.exprs.has("inner") ? num(node, "inner", state, r / 2) : undefined;
    drawOutline(node, under(state, local), [{ points: S.starPoints(cx, cy, points, r, inner, turn), closed: true }], ink(node, "fill", state), ink(node, "stroke", state));
  },

  Diamond(node, state) {
    const cx = num(node, "cx", state, 0);
    const cy = num(node, "cy", state, 0);
    const w = num(node, "width", state, 10);
    const h = num(node, "height", state, w);
    if (w <= 0 || h <= 0) return;
    const m = S.multiply(state.m, localMatrix(node, state, { x: cx, y: cy }));
    const fill = ink(node, "fill", state);
    const stroke = ink(node, "stroke", state);
    if (!state.sphere && S.isConformal(m) && !node.raw.has("dash") && !node.exprs.has("reveal")) {
      const c = S.apply(m, { x: cx, y: cy });
      const k = S.scaleOf(m);
      const r = { x: c.x - (w * k) / 2, y: c.y - (h * k) / 2, width: w * k, height: h * k };
      const rotation = S.angleOf(m);
      glowRect(node, state, r, Math.min(r.width, r.height) / 2);
      if (fill) state.env.rc.fillDiamond(r, flat(fill), { rotation });
      if (stroke) state.env.rc.fillDiamond(r, flat(stroke), { rotation, border: num(node, "strokeWidth", state, 1) * k });
      return;
    }
    const pts = [
      { x: cx, y: cy - h / 2 },
      { x: cx + w / 2, y: cy },
      { x: cx, y: cy + h / 2 },
      { x: cx - w / 2, y: cy },
    ];
    drawOutline(node, { ...state, m }, [{ points: pts, closed: true }], fill, stroke);
  },

  Text(node, state) {
    const v = value(node, "value", state);
    let text = node.raw.get("text") ?? "";
    if (v !== undefined) {
      const decimals = num(node, "decimals", state, 0);
      const n = toNumber(v);
      const shown = n.toFixed(Math.max(0, Math.min(6, Math.floor(decimals))));
      const format = node.raw.get("format");
      text = format ? format.replace(/#/g, shown) : shown;
    }
    if (!text) return;
    const { rc, theme } = state.env;
    const m = S.multiply(state.m, localMatrix(node, state, { x: 0, y: 0 }));
    const k = S.scaleOf(m);
    const base = theme.metrics.font;
    const font: FontSpec = {
      family: node.raw.get("family") ?? base.family,
      size: Math.max(1, num(node, "size", state, base.size) * k),
      weight: node.exprs.has("weight") ? num(node, "weight", state, 400) : (base.weight ?? 400),
    };
    const fill = ink(node, "fill", state);
    const color = fill ? flat(fill) : theme.palette.text;
    const at = S.apply(m, { x: 0, y: 0 });
    const align = node.raw.get("align") ?? "start";
    const width = align === "start" ? 0 : rc.measureWidth(text, font);
    const x = align === "center" ? at.x - width / 2 : align === "end" ? at.x - width : at.x;
    rc.drawText(text, x, at.y, font, color, { baseline: "middle" });
  },

  Image(node, state) {
    const src = node.raw.get("src");
    const tex = src ? state.env.image?.(src) : null;
    if (!tex) return;
    const w = num(node, "width", state, tex.width);
    const h = num(node, "height", state, tex.height);
    drawPicture(node, state, tex, w, h, undefined);
  },

  Sprite(node, state) {
    const src = node.raw.get("src");
    const tex = src ? state.env.image?.(src) : null;
    if (!tex) return;
    const fw = num(node, "frameWidth", state, tex.height);
    const fh = num(node, "frameHeight", state, fw);
    if (fw <= 0 || fh <= 0) return;
    const columns = Math.max(1, Math.floor(tex.width / fw));
    const rows = Math.max(1, Math.floor(tex.height / fh));
    const [first, last] = frameRange(node.raw.get("frames"), columns * rows);
    const count = last - first + 1;
    let index: number;
    if (node.exprs.has("frame")) {
      index = Math.floor(num(node, "frame", state, 0));
    } else {
      const fps = num(node, "fps", state, 12);
      const loop = num(node, "loop", state, 1) !== 0;
      const f = Math.floor(state.env.t * fps);
      index = loop ? f : Math.min(f, count - 1);
    }
    const k = first + (((index % count) + count) % count);
    const src2 = { x: (k % columns) * fw, y: Math.floor(k / columns) * fh, width: fw, height: fh };
    drawPicture(node, state, tex, num(node, "width", state, fw), num(node, "height", state, fh), src2);
  },

  Sphere(node, state) {
    const cx = num(node, "cx", state, 0);
    const cy = num(node, "cy", state, 0);
    const r = num(node, "r", state, 100);
    if (r <= 0) return;
    const rad = Math.PI / 180;
    const turn = num(node, "turn", state, 0) * rad;
    const pitch = num(node, "pitch", state, 0) * rad;
    const roll = num(node, "roll", state, 0) * rad;
    const side = (node.raw.get("side") ?? "front") as Projection["side"];
    const sphere: Projection = {
      side,
      pm: S.IDENTITY,
      project: (p) => {
        const q = onSphere(p.y * rad, p.x * rad, turn, pitch, roll);
        return { x: cx + q.x * r, y: cy + q.y * r, z: q.z };
      },
    };
    const m = S.multiply(state.m, localMatrix(node, state, { x: cx, y: cy }));
    children(node, { ...state, m, sphere, scope: withVars(state.scope, { radius: r }) });
  },

  Meridians(node, state) {
    const n = Math.max(1, Math.min(180, Math.floor(num(node, "count", state, 12))));
    const samples = Math.max(4, Math.floor(num(node, "samples", state, 24)));
    const paths: S.Subpath[] = [];
    for (let k = 0; k < n; k++) {
      const lon = (k / n) * 360;
      paths.push({ points: Array.from({ length: samples + 1 }, (_, i) => ({ x: lon, y: -90 + (i / samples) * 180 })), closed: false });
    }
    drawOutline(node, state, paths, null, ink(node, "stroke", state));
  },

  Parallels(node, state) {
    const step = Math.max(1, num(node, "step", state, 30));
    const limit = Math.min(89.9, num(node, "limit", state, 60));
    const samples = Math.max(8, Math.floor(num(node, "samples", state, 48)));
    const paths: S.Subpath[] = [];
    for (let lat = -limit; lat <= limit + 1e-9; lat += step) {
      paths.push({ points: Array.from({ length: samples + 1 }, (_, i) => ({ x: (i / samples) * 360, y: lat })), closed: false });
    }
    drawOutline(node, state, paths, null, ink(node, "stroke", state));
  },

  Walk(node, state) {
    const steps = Math.max(1, Math.min(2000, Math.floor(num(node, "steps", state, 8))));
    let x = num(node, "x", state, 0);
    let y = num(node, "y", state, 0);
    let heading = num(node, "heading", state, 0);
    const limit = num(node, "limit", state, 77);
    const points: Point[] = [{ x, y }];
    for (let k = 0; k < steps; k++) {
      const scope = withVars(state.scope, { k, __copy: copyKey(state, k, 5) });
      const at = { ...state, scope };
      let dx: number;
      let dy: number;
      if (node.exprs.has("dx") || node.exprs.has("dy")) {
        dx = num(node, "dx", at, 0);
        dy = num(node, "dy", at, 0);
      } else {
        heading += num(node, "turn", at, 0);
        const len = num(node, "step", at, 10);
        const h = (heading * Math.PI) / 180;
        dx = Math.cos(h) * len;
        dy = Math.sin(h) * len;
      }
      // On a sphere a degree of longitude shrinks toward the poles; a walk keeps its pace.
      if (state.sphere) dx /= Math.max(0.3, Math.cos((y * Math.PI) / 180));
      x += dx;
      y += dy;
      if (state.sphere) y = Math.max(-limit, Math.min(limit, y));
      points.push({ x, y });
    }
    drawOutline(node, state, [{ points, closed: false }], null, ink(node, "stroke", state));
  },

  Orbit(node, state) {
    const p = orbitAt(node, state, num(node, "angle", state, 0));
    const side = node.raw.get("side") ?? "both";
    if ((side === "front" && p.z < 0) || (side === "back" && p.z >= 0)) return;
    const scope = withVars(state.scope, {
      z: p.z,
      angle: num(node, "angle", state, 0),
      ox: p.x - num(node, "cx", state, 0),
      oy: p.y - num(node, "cy", state, 0),
    });
    const copy = { ...state, scope };
    children(node, under(copy, S.multiply(S.translation(p.x, p.y), localMatrixNoMove(node, copy))));
  },

  OrbitPath(node, state) {
    const samples = Math.max(8, Math.floor(num(node, "samples", state, 64)));
    const side = node.raw.get("side") ?? "both";
    const pts = Array.from({ length: samples + 1 }, (_, i) => orbitAt(node, state, (i / samples) * 360));
    const runs = side === "both" ? [pts] : S_visibleRuns(pts, side === "front");
    drawOutline(node, state, runs.map((r) => ({ points: r, closed: false })), null, ink(node, "stroke", state));
  },
};

function S_visibleRuns<P extends { z: number }>(points: readonly P[], near: boolean): P[][] {
  const runs: P[][] = [];
  let run: P[] = [];
  for (const p of points) {
    if (p.z >= 0 === near) run.push(p);
    else {
      if (run.length > 1) runs.push(run);
      run = [];
    }
  }
  if (run.length > 1) runs.push(run);
  return runs;
}

function orbitAt(node: DrawNode, state: State, angle: number): { x: number; y: number; z: number } {
  const rad = Math.PI / 180;
  const cx = num(node, "cx", state, 0);
  const cy = num(node, "cy", state, 0);
  const r = num(node, "r", state, 100);
  const q = orbitPoint(num(node, "incline", state, 60) * rad, num(node, "node", state, 0) * rad, angle * rad);
  return { x: cx + q.x * r, y: cy + q.y * r, z: q.z };
}

/** A generator's per-copy rotate and scale, about the copy's own origin; its x and y place the area. */
function localMatrixNoMove(node: DrawNode, state: State): S.Matrix {
  const rotate = num(node, "rotate", state, 0);
  const scale = num(node, "scale", state, 1);
  let m: S.Matrix = S.IDENTITY;
  if (rotate !== 0) m = S.rotation(rotate);
  if (scale !== 1) m = S.multiply(m, S.scaling(scale));
  return m;
}

function centerOf(points: readonly Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const p of points) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  return { x: (x0 + x1) / 2, y: (y0 + y1) / 2 };
}

function frameRange(text: string | undefined, total: number): [number, number] {
  if (!text) return [0, Math.max(0, total - 1)];
  const m = /^\s*(\d+)\s*(?:-\s*(\d+))?\s*$/.exec(text);
  if (!m) return [0, Math.max(0, total - 1)];
  const a = Math.min(total - 1, Number(m[1]));
  const b = m[2] === undefined ? a : Math.min(total - 1, Number(m[2]));
  return [Math.min(a, b), Math.max(a, b)];
}

function drawPicture(node: DrawNode, state: State, tex: DrawableTexture, w: number, h: number, src: Rect | undefined): void {
  if (w <= 0 || h <= 0) return;
  const m = S.multiply(state.m, localMatrix(node, state, { x: w / 2, y: h / 2 }));
  if (!S.isAxisAligned(m)) return;
  const a = S.apply(m, { x: 0, y: 0 });
  const b = S.apply(m, { x: w, y: h });
  const tint = node.exprs.has("tint") ? toColor(value(node, "tint", state)!) : undefined;
  state.env.rc.drawTexture(tex, { x: a.x, y: a.y, width: b.x - a.x, height: b.y - a.y }, src, tint);
}

/**
 * Paint a definition filling the environment's box, its parameters given as values. A definition
 * with a viewBox is fitted into the box; one without is drawn at the box's own size.
 */
export function paintDefinition(def: Definition, given: Record<string, Value>, env: ArtEnvironment, fit: "contain" | "cover" | "stretch" | "none" = "contain"): void {
  const root = rootScope(env);
  let m = env.matrix ?? S.IDENTITY;
  if (def.viewBox) m = S.multiply(m, fitMatrix(def.viewBox, env.w, env.h, fit));
  const scope = definitionScope(def, root, given, def.viewBox?.width ?? env.w, def.viewBox?.height ?? env.h);
  paintList(def.drawing.nodes, { env, m, scope, root, sphere: null, depth: 1 });
}

/** The scope a definition's body is drawn in: the root, its size, and its parameters. */
export function definitionScope(def: Definition, root: Scope, given: Record<string, Value>, w: number, h: number): Scope {
  const vars: Record<string, Value> = { w, h };
  const scope = withVars(root, vars);
  for (const p of def.params) {
    vars[p.name] = Object.hasOwn(given, p.name) ? given[p.name]! : p.value.evaluate(scope);
  }
  return scope;
}

/** The matrix that fits `box` into a `w` by `h` area. */
export function fitMatrix(box: Rect, w: number, h: number, fit: "contain" | "cover" | "stretch" | "none"): S.Matrix {
  if (fit === "none") return S.translation(-box.x, -box.y);
  if (fit === "stretch") return S.multiply(S.scaling(w / box.width, h / box.height), S.translation(-box.x, -box.y));
  const k = fit === "cover" ? Math.max(w / box.width, h / box.height) : Math.min(w / box.width, h / box.height);
  const ox = (w - box.width * k) / 2;
  const oy = (h - box.height * k) / 2;
  return S.multiply(S.translation(ox, oy), S.multiply(S.scaling(k), S.translation(-box.x, -box.y)));
}

// --- the shared drawing routines -----------------------------------------------------------------

function glowOf(node: DrawNode, state: State): { color: Color; size: number } | null {
  if (!node.exprs.has("glow") && !node.animations.some((a) => a.attr === "glow")) return null;
  const v = value(node, "glow", state);
  if (v === undefined) return null;
  const color = toColor(v);
  const size = num(node, "glowSize", state, 12);
  return color.a > 0 && size > 0 ? { color, size } : null;
}

function glowRect(node: DrawNode, state: State, r: Rect, radius: number): void {
  const g = glowOf(node, state);
  if (!g) return;
  const k = S.scaleOf(state.m);
  state.env.rc.fillGlow(r, g.color, g.size * k, radius);
}

function glowArc(node: DrawNode, state: State, c: Point, r0: number, r1: number, lo: number, hi: number): void {
  const g = glowOf(node, state);
  if (!g) return;
  const k = S.scaleOf(state.m);
  const spread = g.size * k;
  state.env.rc.fillArc(c, Math.max(0, r0 - spread / 2), r1 + spread / 2, lo, hi, fade(g.color, 0.18));
  state.env.rc.fillArc(c, Math.max(0, r0 - spread), r1 + spread, lo, hi, fade(g.color, 0.08));
}

function fillRect(rc: RenderContext, r: Rect, fill: Ink, radius: number): void {
  if (fill.kind === "solid") rc.fillRect(r, fill.color, radius);
  else if (fill.kind === "linear") rc.fillGradient(r, fill.from, fill.to, axisOf(fill.angle), radius);
  else rc.fillRadialGradient(r, fill.from, fill.to, fill.center, fill.radii, radius);
}

/** A circle the renderer can draw as itself: filled, swept, ringed or dashed. */
function roundShape(
  node: DrawNode, state: State, c: Point, r: number, turn: number,
  fill: Ink | null, stroke: Ink | null, units: number,
): void {
  const { rc } = state.env;
  const reveal = Math.min(1, num(node, "reveal", state, 1));
  if (reveal <= 0) return;
  const start = (num(node, "start", state, 0) * Math.PI) / 180 + turn;
  const sweep = Math.PI * 2 * reveal;
  const box = { x: c.x - r, y: c.y - r, width: r * 2, height: r * 2 };
  const g = glowOf(node, state);
  if (g) rc.fillGlow(box, g.color, g.size * (r / units), r);
  if (fill) {
    if (reveal < 1) rc.fillArc(c, 0, r, start, start + sweep, flat(fill));
    else if (fill.kind === "solid") rc.fillCircle(c, r, fill.color);
    else fillRect(rc, box, fill, r);
  }
  if (stroke) {
    const k = r / units;
    const sw = Math.max(0.1, num(node, "strokeWidth", state, 1) * k);
    const color = flat(stroke);
    const dash = dashPattern(node);
    if (dash) {
      const offset = num(node, "dashOffset", state, 0);
      const circumference = Math.PI * 2 * units;
      const period = dash.reduce((s, n) => s + n, 0);
      const pattern = dash.length % 2 === 1 ? [...dash, ...dash] : dash;
      let s = -(((offset % period) + period) % period);
      let i = 0;
      const limit = circumference * reveal;
      while (s < limit && i < 4000) {
        const len = pattern[i % pattern.length]!;
        if (i % 2 === 0) {
          const a = Math.max(0, s);
          const b = Math.min(limit, s + len);
          if (b > a) rc.fillArc(c, r - sw / 2, r + sw / 2, start + (a / units), start + (b / units), color);
        }
        s += len;
        i++;
      }
    } else if (reveal < 1) {
      rc.fillArc(c, r - sw / 2, r + sw / 2, start, start + sweep, color);
    } else {
      rc.strokeCircle(c, r, sw, color);
    }
  }
}

/** A circle on a sphere: its center goes round with the sphere, its size does not. */
function dot(node: DrawNode, state: State, lon: number, lat: number, r: number): void {
  const sphere = state.sphere!;
  const p = sphere.project(S.apply(sphere.pm, { x: lon, y: lat }));
  if ((sphere.side === "front" && p.z < 0) || (sphere.side === "back" && p.z >= 0)) return;
  const at = { ...state, scope: withVars(state.scope, { z: p.z }) };
  const c = S.apply(state.m, p);
  const k = S.scaleOf(state.m);
  roundShape(node, at, c, r * k, 0, ink(node, "fill", at), ink(node, "stroke", at), r);
}

function dashPattern(node: DrawNode): number[] | null {
  const text = node.raw.get("dash");
  if (!text) return null;
  const n = text.split(/[\s,]+/).filter(Boolean).map(Number).filter((v) => Number.isFinite(v) && v > 0);
  return n.length > 0 ? n : null;
}

/**
 * Fill and stroke an outline given in node units: the general path every shape falls back to.
 * On a sphere it is projected and only the runs on the side being drawn survive, each with its own
 * depth in scope as `z` - which is how a seam fades toward the limb.
 */
function drawOutline(
  node: DrawNode, state: State, paths: readonly S.Subpath[],
  fill: Ink | null, stroke: Ink | null, keepFillOpen = false,
): void {
  const { rc } = state.env;
  const k = S.scaleOf(state.m);
  if (state.sphere) {
    const sphere = state.sphere;
    for (const path of paths) {
      const ring = path.closed ? [...path.points, path.points[0]!] : path.points;
      const projected = ring.map((p) => sphere.project(S.apply(sphere.pm, p)));
      const runs = sphere.side === "both" ? [projected] : S_visibleRuns(projected, sphere.side === "front");
      for (const run of runs) {
        const z = run.reduce((s, p) => s + p.z, 0) / run.length;
        const at = { ...state, scope: withVars(state.scope, { z }) };
        const pts = run.map((p) => S.apply(state.m, p));
        const f = fill ? ink(node, "fill", at) : null;
        const s = stroke ? ink(node, "stroke", at) : null;
        if (f && path.closed && run.length === projected.length) rc.fillTriangles(S.triangulate(pts), flat(f));
        if (s) strokeRuns(node, at, [{ points: pts, closed: false }], flat(s), k);
      }
    }
    return;
  }
  const local = paths.map((p) => ({ points: S.applyAll(state.m, p.points), closed: p.closed }));
  const g = glowOf(node, state);
  if (fill) {
    const rings = local.filter((p) => p.closed || keepFillOpen).map((p) => p.points);
    if (g && !stroke) {
      for (const ring of rings) {
        rc.fillTriangles(S.strokeTriangles(ring, true, g.size * k), fade(g.color, 0.12));
        rc.fillTriangles(S.strokeTriangles(ring, true, g.size * k * 0.45), fade(g.color, 0.2));
      }
    }
    if (rings.length > 0) rc.fillTriangles(S.fillRings(rings), flat(fill));
  }
  if (stroke) strokeRuns(node, state, local, flat(stroke), k);
}

/** Stroke local-px subpaths with the node's width, dashes, reveal, caps and glow. */
function strokeRuns(node: DrawNode, state: State, paths: readonly S.Subpath[], color: Color, k: number): void {
  const { rc } = state.env;
  const width = Math.max(0.1, num(node, "strokeWidth", state, 1) * k);
  let runs: S.Subpath[] = [...paths];
  const reveal = node.exprs.has("reveal") || node.animations.some((a) => a.attr === "reveal") ? num(node, "reveal", state, 1) : 1;
  if (reveal <= 0) return;
  if (reveal < 1) {
    // Drawn in order: the second stroke of a star starts once the first is finished.
    const lengths = runs.map((p) => S.polylineLength(p.points, p.closed));
    const total = lengths.reduce((s, n) => s + n, 0);
    let left = total * reveal;
    const out: S.Subpath[] = [];
    runs.forEach((p, i) => {
      if (left <= 0) return;
      const len = lengths[i]!;
      if (left >= len) out.push(p);
      else out.push({ points: S.trimPolyline(p.points, p.closed, 0, left / len), closed: false });
      left -= len;
    });
    runs = out;
  }
  const dash = dashPattern(node);
  if (dash) {
    const offset = num(node, "dashOffset", state, 0) * k;
    runs = runs.flatMap((p) => S.dashPolyline(p.points, p.closed, dash.map((d) => d * k), offset).map((points) => ({ points, closed: false })));
  }
  const round = (node.raw.get("cap") ?? "round") === "round";
  const g = glowOf(node, state);
  const pass = (w: number, c: Color) => {
    for (const run of runs) {
      if (run.points.length < 2) continue;
      if (!run.closed && round && run.points.length === 2) {
        rc.drawLine(run.points[0]!, run.points[1]!, w, c);
        continue;
      }
      rc.fillTriangles(S.strokeTriangles(run.points, run.closed, w), c);
      if (!run.closed && round && w > 1.5) {
        rc.fillCircle(run.points[0]!, w / 2, c);
        rc.fillCircle(run.points[run.points.length - 1]!, w / 2, c);
      }
    }
  };
  if (g) {
    pass(width + g.size * k, fade(g.color, 0.1));
    pass(width + g.size * k * 0.4, fade(g.color, 0.22));
  }
  pass(width, color);
}

/** The outline of a drawing's filled shapes, in local px: what a shaped control is hit tested and glowed against. */
export function outlineOf(drawing: Drawing, env: ArtEnvironment): Point[][] {
  const rings: Point[][] = [];
  const root = rootScope(env);
  const visit = (node: DrawNode, state: State) => {
    const local = (pts: Point[]) => rings.push(S.applyAll(state.m, pts));
    const n = (name: string, d: number) => num(node, name, state, d);
    switch (node.tag) {
      case "Group":
      case "Clip":
        node.children.forEach((c) => visit(c, under(state, localMatrix(node, state, { x: 0, y: 0 }))));
        return;
      case "Rect":
        local(S.rectPoints(n("x", 0), n("y", 0), n("width", 0), n("height", 0), n("rx", 0), n("ry", n("rx", 0))));
        return;
      case "Circle":
        local(S.ellipsePoints(n("cx", 0), n("cy", 0), n("r", 0), n("r", 0), 1));
        return;
      case "Ellipse":
        local(S.ellipsePoints(n("cx", 0), n("cy", 0), n("rx", 0), n("ry", n("rx", 0)), 1));
        return;
      case "Polygon":
        local(pointsOf(node, state));
        return;
      case "Star":
        local(S.starPoints(n("cx", 0), n("cy", 0), n("points", 5), n("r", 0), node.exprs.has("inner") ? n("inner", 0) : undefined, (n("turn", 0) * Math.PI) / 180));
        return;
      case "Diamond": {
        const cx = n("cx", 0);
        const cy = n("cy", 0);
        const w = n("width", 10);
        const h = n("height", w);
        local([{ x: cx, y: cy - h / 2 }, { x: cx + w / 2, y: cy }, { x: cx, y: cy + h / 2 }, { x: cx - w / 2, y: cy }]);
        return;
      }
      case "Path":
        for (const p of node.cache.path ?? []) if (p.closed) local(p.points);
        return;
    }
  };
  const state: State = { env, m: env.matrix ?? S.IDENTITY, scope: root, root, sphere: null, depth: 0 };
  for (const node of drawing.nodes) visit(node, state);
  return rings.filter((r) => r.length >= 3);
}
